import { faExpand } from '@fortawesome/pro-solid-svg-icons';
import { getFieldValue as getNestedFieldValue } from '@supply-mover-2-0/core';
import React, { useCallback, useState } from 'react';
import { Icon } from '../../../components/base/Icon';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { useListLinkNavigation } from '../../hooks/useListLinkNavigation';
import type {
	EnhancedFieldConfiguration,
	ListBuilderCallbacks,
} from '../../types';
import { SIZE_MAP } from '../../utils/colorRuleEngine';
import { TextExpansionModal } from './TextExpansionModal';
import {
	getFieldDisplayProperties,
	resolveTextDisplayColor,
} from './displayHelpers';
import { handleTruncationTooltip } from './truncationTooltip';

/**
 * Check if a URL template can be fully resolved with the given data.
 * Returns false if any placeholder would resolve to null/undefined/empty.
 */
function canResolveUrlTemplate(
	template: string | undefined,
	data: Record<string, unknown>,
): boolean {
	if (template == null) {
		return false;
	}

	const matches = template.match(/\{([^}]+)\}/g);

	if (matches == null) {
		return true; // No placeholders, template is valid as-is
	}

	for (const match of matches) {
		const fieldPath = match.slice(1, -1);
		const value = getNestedFieldValue(data, fieldPath);

		if (value == null || String(value) === '') {
			return false;
		}
	}

	return true;
}

interface TextDisplayProps {
	field: EnhancedFieldConfiguration;
	value: any;
	data: any;
	listKey?: string;
	objects?: Array<{ objectTable: string; baseUrl?: string }>;
	callbacks?: ListBuilderCallbacks;
}

export function TextDisplay({
	field,
	value,
	data,
	listKey,
	objects,
	callbacks,
}: TextDisplayProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();
	const { styleConfig, link } = field;
	const [isExpansionModalOpen, setIsExpansionModalOpen] = useState(false);

	// Use the shared link navigation hook
	const { linkUrl, handleLinkClick, isCallback } = useListLinkNavigation({
		field,
		data,
		listKey,
		objects,
		callbacks,
	});

	// Get display value and color using value mapping
	const { displayValue, colorValue } = getFieldDisplayProperties(
		field,
		value,
		data,
	);

	// Add prefix and suffix if configured, but only when there's an actual value
	// (not when displaying the default value)
	const prefix = field.prefix ?? '';
	const suffix = field.suffix ?? '';
	const defaultValue = field.dataConfig?.defaultValue ?? '';
	const isDefaultValue = displayValue === defaultValue;
	const formattedValue =
		displayValue != null && displayValue !== '' && !isDefaultValue
			? `${prefix}${displayValue}${suffix}`
			: displayValue;

	// Text expansion configuration. formatAsJson / showFormattedInModal are
	// forward-compatible knobs read here but not (yet) part of the
	// StyleConfiguration.textExpansion type; view through a wider shape.
	const textExpansion:
		| {
				enabled: boolean;
				maxLines?: number;
				formatAsJson?: boolean;
				showFormattedInModal?: boolean;
		  }
		| undefined = styleConfig?.textExpansion;
	const maxLines = textExpansion?.enabled ? textExpansion?.maxLines || 2 : 1;

	// Character thresholds based on maxLines
	// For line-clamp-1, we use a very low threshold because URLs and long text
	// can wrap unpredictably, causing visual truncation even with fewer characters
	const CHARACTER_THRESHOLDS = {
		1: 20, // Very conservative for single line - URLs truncate unpredictably
		2: 60, // ~60 chars for 2 lines
		3: 120, // ~120 chars for 3 lines
	};

	const charThreshold =
		CHARACTER_THRESHOLDS[maxLines as keyof typeof CHARACTER_THRESHOLDS] ||
		CHARACTER_THRESHOLDS[2];

	// Format as JSON if configured
	const formatAsJson = textExpansion?.formatAsJson ?? false;
	const showFormattedInModal = textExpansion?.showFormattedInModal ?? false;

	// For table display: show unformatted if showFormattedInModal is true, otherwise format
	const tableValue =
		formatAsJson && typeof displayValue === 'object' && displayValue != null
			? showFormattedInModal
				? JSON.stringify(displayValue)
				: JSON.stringify(displayValue, null, 2)
			: formattedValue;

	// For modal display: always show formatted if formatAsJson is true
	const modalValue =
		formatAsJson && typeof displayValue === 'object' && displayValue != null
			? JSON.stringify(displayValue, null, 2)
			: formattedValue;

	// Determine if text should show expansion icon based on character count
	const textLength = String(displayValue || '').length;
	const isLongEnoughForExpansion = textLength > charThreshold;

	// Show expansion button if:
	// 1. Text expansion is enabled AND
	// 2. The value is not empty or just a placeholder AND
	// 3. The text length exceeds the character threshold
	const expansionEnabled =
		textExpansion?.enabled &&
		displayValue != null &&
		displayValue !== '' &&
		displayValue !== '-' &&
		isLongEnoughForExpansion;

	// Get size configuration - fallback to 'md' if size key is not found in SIZE_MAP
	const sizeConfig = SIZE_MAP[styleConfig?.size ?? 'md'] ?? SIZE_MAP.md;

	// Handle expansion modal
	const handleToggleExpansion = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setIsExpansionModalOpen(!isExpansionModalOpen);
		},
		[isExpansionModalOpen],
	);

	// Helper to get record ID for link enabled check
	const getRecordId = (record: unknown, idField?: string): unknown => {
		if (record == null) {
			return null;
		}

		const rec = record as Record<string, unknown>;

		if (idField != null) {
			return rec[idField] ?? null;
		}

		return (
			rec['id'] ??
			rec['_id'] ??
			rec['Id'] ??
			rec['ID'] ??
			rec['clientId'] ??
			rec['client_id']
		);
	};

	// Check if this field has linking enabled
	const recordId = getRecordId(data, link?.idField);
	// For custom links with urlTemplate, verify all placeholders can be resolved
	const canResolveCustomUrl =
		link?.type === 'custom' &&
		(link.customUrl != null ||
			canResolveUrlTemplate(link.urlTemplate, data as Record<string, unknown>));

	const isLinkConfigured =
		link?.enabled &&
		(link.type === 'callback' ||
			(link.type === 'self' && recordId != null) ||
			canResolveCustomUrl ||
			(link.type === 'object' && recordId != null));

	// useListLinkNavigation may suppress the URL (e.g. for client-detail rows
	// the caller can't open). When that happens we want plain text — no link
	// styling, no underline, no clickable cursor — instead of the disabled
	// link-styled button fallback.
	const isLinkEnabled = isLinkConfigured && (isCallback || linkUrl != null);

	// Apply color and styles
	// For JSON content or text wrapping, we need to allow wrapping to prevent overflow
	const needsWrapping = formatAsJson && showFormattedInModal;
	const shouldWrap = styleConfig?.allowTextWrap || needsWrapping;

	// Single-line cells truncate with real `white-space: nowrap` + ellipsis
	// (`truncate`) instead of `line-clamp-1`. `line-clamp` uses
	// `display: -webkit-box`, which in Brave can mis-size a flex item
	// (`flex-1 min-w-0`) down to ~min-content and — together with
	// `word-break: break-all` — stack even short values like "ACH" vertically,
	// then clamp them to a phantom ellipsis with plenty of room left. `nowrap`
	// can't stack, so the value renders in full whenever it fits and only
	// truncates on genuine horizontal overflow. break-all/overflow-wrap stay on
	// for the wrapping cases (multi-line clamp / allowTextWrap) where they
	// actually prevent overflow.
	const shouldClamp = textExpansion?.enabled || !styleConfig?.allowTextWrap;
	const isSingleLineTruncate = shouldClamp && maxLines === 1;
	const textStyle: React.CSSProperties = {
		color: resolveTextDisplayColor({
			isLinkEnabled: Boolean(isLinkEnabled),
			linkConfigEnabled: Boolean(link?.enabled),
			colorText: colorValue?.text,
			isDark,
			primaryColor: colors.values.primary,
			defaultTextColor: colors.values.textPrimary,
		}),
		fontSize: sizeConfig.fontSize,
		lineHeight: 1.5,
		maxWidth: '100%',
		...(isSingleLineTruncate
			? {}
			: {
					wordBreak: 'break-all', // Break anywhere to prevent overflow
					overflowWrap: 'break-word',
				}),
	};

	// Build the link URL for the expansion modal if linking is enabled
	const expansionLinkUrl =
		isLinkEnabled && link?.type === 'custom' ? link.customUrl : undefined;

	// Render expansion modal
	const expansionModal = expansionEnabled && (
		<TextExpansionModal
			isOpen={isExpansionModalOpen}
			onClose={() => {
				setIsExpansionModalOpen(false);
			}}
			title={t('Full Text')}
			text={String(modalValue)}
			linkUrl={expansionLinkUrl}
			isCodeBlock={formatAsJson && showFormattedInModal}
		/>
	);

	// Single line → `truncate` (nowrap + ellipsis); multi-line → `line-clamp-N`.
	const lineClampClass =
		maxLines === 1
			? 'truncate'
			: maxLines === 2
				? 'line-clamp-2'
				: 'line-clamp-3';
	const textClasses = cn(
		shouldClamp ? lineClampClass : '',
		needsWrapping ? 'overflow-hidden' : '',
	);

	if (isLinkEnabled) {
		// Callback links have no URL — render as button; navigation links render as anchor
		const linkContent = formatAsJson
			? String(tableValue)
			: t(String(tableValue));

		const linkElement =
			!isCallback && linkUrl != null ? (
				<a
					href={linkUrl}
					onClick={handleLinkClick}
					onMouseEnter={handleTruncationTooltip}
					className={cn(
						textClasses,
						'hover:underline transition-all cursor-pointer',
					)}
					style={textStyle}
				>
					{linkContent}
				</a>
			) : (
				<button
					onClick={handleLinkClick}
					onMouseEnter={handleTruncationTooltip}
					className={cn(
						textClasses,
						'hover:underline transition-all cursor-pointer text-left',
					)}
					style={textStyle}
					type="button"
				>
					{linkContent}
				</button>
			);

		return (
			<div className="flex items-start justify-between min-w-0 gap-2">
				{linkElement}
				{expansionEnabled === true ? (
					<button
						onClick={handleToggleExpansion}
						className="py-0.5 hover:opacity-70 transition-opacity flex-shrink-0"
						type="button"
					>
						<Icon
							icon={faExpand}
							size={12}
							color={colors.values.textSecondary}
						/>
					</button>
				) : null}
				{expansionModal}
			</div>
		);
	}

	return (
		<div className="flex items-start justify-between min-w-0 gap-2">
			<span
				className={cn(textClasses, 'min-w-0 flex-1')}
				style={textStyle}
				onMouseEnter={handleTruncationTooltip}
			>
				{formatAsJson ? String(tableValue) : t(String(tableValue))}
			</span>
			{expansionEnabled && (
				<button
					onClick={handleToggleExpansion}
					className="py-0.5 hover:opacity-70 transition-opacity flex-shrink-0"
					type="button"
				>
					<Icon icon={faExpand} size={12} color={colors.values.textSecondary} />
				</button>
			)}
			{expansionModal}
		</div>
	);
}
